import clsx from 'clsx';
import React, { type ReactElement } from 'react';

import { Theme } from '@interfaces/global';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

export interface Offer {
  code: string;
  currentPrice: string;
  isPro?: boolean;
  originalPrice?: string;
  shortDescription: string;
  title: string;
}

export type SelectedOffer = Pick<Offer, 'code' | 'isPro'>;

interface OfferProps {
  contentContainerClassName?: string;
  offer: Offer;
  selectedOfferCode: string;
  setSelectedOffer?: (plan: SelectedOffer) => void;
  theme?: Theme;
}

const OfferOption = ({
  contentContainerClassName,
  offer: { code, currentPrice, isPro, originalPrice, shortDescription, title },
  selectedOfferCode,
  setSelectedOffer,
  theme = Theme.Dark,
}: OfferProps): ReactElement => {
  const isSelected = selectedOfferCode === code;

  const handleOfferSelection = () => {
    setSelectedOffer({ code, isPro });
  };

  return (
    <div
      id={code}
      onClick={handleOfferSelection}
      className="flex max-w-[272px] cursor-pointer items-start sm:w-[240px] md:w-[252px]"
    >
      <div
        className={clsx(
          'mr-8 flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-full border border-solid p-4',
          {
            'border-grayscale-gray': !isSelected || (isSelected && theme === Theme.Light),
            'border-white': isSelected && theme !== Theme.Light,
          }
        )}
      >
        <span
          className={clsx('h-full w-full rounded-full', {
            block: isSelected,
            hidden: !isSelected,
            'bg-pro-gold': isPro,
            'bg-primary-magenta': !isPro,
          })}
        />
      </div>
      <div className={contentContainerClassName}>
        <h2
          className={clsx('m-0 font-suisse-intl text-button-sm', {
            'text-pro-gold': isPro,
            'text-primary-magenta': !isPro,
          })}
        >
          <span className="font-bold">{title}</span>
          <span className="ml-8 font-bold">${currentPrice}</span>
          {originalPrice && currentPrice != originalPrice && (
            <span
              className={clsx('ml-8 font-light line-through', {
                'text-white': theme === Theme.Dark,
                'text-grayscale-gray': theme === Theme.Light,
              })}
            >
              ${originalPrice}
            </span>
          )}
        </h2>
        <p
          className={clsx('mb-0 mt-4 font-suisse-intl text-button-xs', {
            'text-grayscale-beige': theme === Theme.Dark,
            'font-light text-grayscale-charcoal': theme === Theme.Light,
          })}
        >
          {AuthoredContentReactToHtmlParser(shortDescription)}
        </p>
      </div>
    </div>
  );
};

export default OfferOption;
